<template>
  <v-card flat class="pa-6 full-width fill-height d-flex flex-column" to="/settings/members">

    <!-- Header -->
    <div class="d-flex align-center">
      <v-avatar rounded="lg" size="52" color="#e9eef5" class="mr-4">
        <v-icon color="primary">fa-users</v-icon>
      </v-avatar>
      <div>
        <div class="text-h6 font-weight-bold">Team access</div>
        <div class="text-body-2 text--secondary mt-1" v-if="!loading">
          {{ summary }}
        </div>
        <v-skeleton-loader
          v-else
          type="text"
          max-width="320"
          class="mt-2"
        />
      </div>
    </div>

    <v-divider class="my-5" />

    <!-- One column per role group -->
    <v-row>
      <v-col
        v-for="group in roleGroups"
        :key="group.label"
        cols="12"
        md="4"
      >
        <div class="d-flex align-center mb-3">
          <span class="text-subtitle-2 font-weight-bold text-no-wrap">{{ group.label }}</span>
          <span class="text--secondary text-body-2 ml-2" v-if="!loading">{{ group.users.length }}</span>
        </div>

        <div class="d-flex flex-wrap" v-if="loading">
          <v-skeleton-loader
            type="avatar"
            loading
            class="mr-1"
          />
        </div>

        <div class="d-flex align-center avatar-stack" v-else-if="group.users.length > 0">
          <div
            v-for="record in group.users"
            :key="record.id"
            class="avatar-wrap"
          >
            <user-avatar :record-id="record.user_id" />
          </div>
        </div>

        <div class="text-body-2 text--secondary" v-else-if="group.empty">
          {{ group.empty }}
        </div>
      </v-col>
    </v-row>

    <!-- Manage users CTA (the whole card links to /settings/members) -->
    <v-spacer />
    <v-divider class="mb-3" />
    <div
      class="d-flex align-center font-weight-medium text-body-2"
      :class="linkTextClass"
    >
      Manage users
      <v-icon small :color="linkColor" class="ml-2">fa-arrow-right</v-icon>
    </div>

  </v-card>
</template>

<style scoped>
/* Overlap the avatars into a stack; the ring matches the card background so
   overlapping edges stay legible in both light and dark themes. */
.avatar-stack .avatar-wrap:not(:first-child) {
  margin-left: -10px;
}
.avatar-stack ::v-deep .avatar-icon,
.avatar-stack ::v-deep .avatar-text {
  box-shadow: 0 0 0 2px #fff;
}
.theme--dark .avatar-stack ::v-deep .avatar-icon,
.theme--dark .avatar-stack ::v-deep .avatar-text {
  box-shadow: 0 0 0 2px #1e1e1e;
}
</style>

<script>
import UserAvatar from '@/components/UserAvatar'
import { AccountUser, User } from '@/models'
import roles from 'shared/permissions/roles.js'

export default {
  components: {
    UserAvatar
  },
  data () {
    return {
      loading: false
    }
  },
  computed: {
    // The brand primary blue is too dark on dark backgrounds, so use a lighter
    // blue for the link there (matches the WorkspaceCard CTA).
    linkColor () {
      return this.$vuetify.theme.dark ? 'light-blue lighten-2' : 'primary'
    },
    linkTextClass () {
      return this.$vuetify.theme.dark ? 'light-blue--text text--lighten-2' : 'primary--text'
    },
    accountUsers () {
      return AccountUser.query()
        .where(u => u.id > 0 && u.account_id + '' === this.$store.getters.account?.id + '')
        .get()
        .map(record => {
          return {
            ...record,
            roles: (record.roles || '').split(',')
              .filter(v => !!v && roles[v]),
            user: User.find(record.user_id)
          }
        })
    },
    adminUsers () {
      return this.accountUsers.filter(record => (record.roles || []).includes('ADMIN'))
    },
    contentUsers () {
      return this.accountUsers.filter(record => {
        const userRoles = record.roles || []
        return userRoles.includes('CONTENT') && !userRoles.includes('ADMIN')
      })
    },
    technicalUsers () {
      return this.accountUsers.filter(record => {
        const userRoles = record.roles || []
        return userRoles.includes('TECHNICAL') && !userRoles.includes('ADMIN')
      })
    },
    roleGroups () {
      const groups = [
        { label: 'Admins', users: this.adminUsers }
      ]
      if (this.$hasModule('CONTENT')) {
        groups.push({
          label: 'Content creators',
          users: this.contentUsers,
          empty: 'Invite others to help manage your SEO content.'
        })
      }
      if (this.$hasModule('TECHNICAL')) {
        groups.push({
          label: 'Tech users',
          users: this.technicalUsers,
          empty: 'Invite others to help manage your technical SEO.'
        })
      }
      return groups
    },
    summary () {
      const people = this.accountUsers.length
      const roleCount = this.roleGroups.filter(group => group.users.length > 0).length
      const workspace = this.$store.getters.account?.name
      const peopleLabel = `${people} ${people === 1 ? 'person' : 'people'}`
      const roleLabel = `${roleCount} ${roleCount === 1 ? 'role' : 'roles'}`
      return `${peopleLabel} across ${roleLabel}${workspace ? ` in the ${workspace} workspace` : ''}`
    }
  },
  async created () {
    if (this.$hasRole('ADMIN')) {
      this.loading = true
      try {
        await AccountUser.fetchMany()
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
